import { html, raw } from '../core/html.js';

/**
 * The icons the site actually uses, inlined. This replaces a render-blocking
 * ~100 KB Font Awesome stylesheet from a third-party CDN with about 2 KB of markup.
 */
const PATHS = {
  arrowRight: 'M5 12h14M13 6l6 6-6 6',
  arrowLeft: 'M19 12H5M11 18l-6-6 6-6',
  mail: 'M3 7a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z|M3.5 7.5 12 13l8.5-5.5',
  pin: 'M12 21s7-5.4 7-11a7 7 0 1 0-14 0c0 5.6 7 11 7 11z|M12 12.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5z',
  building: 'M4 21V5a1 1 0 0 1 1-1h9a1 1 0 0 1 1 1v16|M15 21V10h4a1 1 0 0 1 1 1v10|M3 21h18|M8 8h3M8 12h3M8 16h3',
  check: 'M4 12.5 9 17.5 20 6.5',
  menu: 'M4 7h16M4 12h16M4 17h16',
  close: 'M6 6l12 12M18 6L6 18',
  external: 'M14 5h5v5|M19 5l-8 8|M18 14v4a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h4',
  gamepad: 'M7 12h4M9 10v4|M6 7h12a4 4 0 0 1 4 4v2a4 4 0 0 1-4 4c-1.5 0-2.3-.8-3-1.5H9c-.7.7-1.5 1.5-3 1.5a4 4 0 0 1-4-4v-2a4 4 0 0 1 4-4z|M16 11.5h.01M18 14h.01',
  monitor: 'M3 5a1 1 0 0 1 1-1h16a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1z|M9 20h6M12 16v4',
  phone: 'M7 3a1 1 0 0 0-1 1v16a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V4a1 1 0 0 0-1-1z|M11 18h2',
  code: 'M9 7l-5 5 5 5M15 7l5 5-5 5',
  search: 'M10.5 17.5a7 7 0 1 0 0-14 7 7 0 0 0 0 14z|M15.5 15.5 21 21',
  rocket: 'M12 3c3.5 2 5 5.5 5 9l-2.5 2.5h-5L7 12c0-3.5 1.5-7 5-9z|M9.5 14.5 7 21l4-1.5M14.5 14.5 17 21l-4-1.5|M12 9.5h.01',
  scan: 'M4 8V5a1 1 0 0 1 1-1h3M16 4h3a1 1 0 0 1 1 1v3M20 16v3a1 1 0 0 1-1 1h-3M8 20H5a1 1 0 0 1-1-1v-3|M7 12h10',
  mic: 'M12 4a2.5 2.5 0 0 1 2.5 2.5v5a2.5 2.5 0 0 1-5 0v-5A2.5 2.5 0 0 1 12 4z|M6 11a6 6 0 0 0 12 0|M12 17v3M9 20h6',
  note: 'M5 4a1 1 0 0 1 1-1h8l5 5v11a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1z|M14 3v5h5|M8 13h7M8 17h4',
  play: 'M6 3.5v17l14-8.5z',
  apple: 'M16.2 12.6c0-2.3 1.9-3.4 2-3.5-1.1-1.6-2.8-1.8-3.4-1.8-1.4-.15-2.8.85-3.5.85-.7 0-1.85-.83-3.05-.8-1.55.02-3 .9-3.8 2.3-1.6 2.8-.4 7 1.15 9.3.77 1.12 1.68 2.38 2.88 2.34 1.15-.05 1.6-.75 3-.75s1.8.75 3.03.72c1.25-.02 2.04-1.14 2.8-2.27.88-1.3 1.25-2.56 1.27-2.62-.03-.01-2.43-.93-2.45-3.7z|M14.3 5.9c.62-.76 1.04-1.8.93-2.85-.9.04-1.98.6-2.62 1.35-.57.67-1.08 1.74-.94 2.76 1 .08 2.02-.5 2.63-1.26',
};

/**
 * @param {string} name  key of PATHS
 * @param {{size?: number, label?: string}} options  omit `label` for decorative icons
 */
export const icon = (name, { size = 20, label } = {}) => {
  const definition = PATHS[name];
  if (!definition) throw new Error(`Unknown icon: ${name}`);
  const filled = name === 'play' || name === 'apple';
  const directional = name === 'arrowRight' || name === 'arrowLeft';
  const shapes = definition
    .split('|')
    .map((d) => `<path d="${d}"${filled ? '' : ' fill="none"'} />`)
    .join('');

  return raw(
    `<svg class="icon${directional ? ' icon--directional' : ''}" width="${size}" height="${size}" viewBox="0 0 24 24" ` +
      `${filled ? 'fill="currentColor"' : 'fill="none" stroke="currentColor" stroke-width="1.75" ' +
      'stroke-linecap="round" stroke-linejoin="round"'} ` +
      (label ? `role="img" aria-label="${label}"` : 'aria-hidden="true"') +
      `>${shapes}</svg>`,
  );
};

export { html };
